import { InternalError } from 'restify-errors';

export const abs = (n: bigint): bigint => (n < 0 ? -n : n);

export const isSafeInteger = (n: bigint | number): boolean => {
  return n >= Number.MIN_SAFE_INTEGER && n <= Number.MAX_SAFE_INTEGER;
};

export function toSafeInteger(n: bigint | number): number;
export function toSafeInteger(
  n: bigint | number | undefined
): number | undefined {
  if (n === undefined) {
    return undefined;
  }
  if (!isSafeInteger(n)) {
    throw new InternalError(
      `cannot safely convert bigint value to number: ${n}`
    );
  }
  return Number(n);
}

export type SubstBigInt<T> = T extends Function
  ? T
  : T extends Array<infer U>
  ? Array<SubstBigInt<U>>
  : T extends object
  ? _DeepSubstBigIntObject<T>
  : T extends bigint
  ? number | string
  : T;
export declare type _DeepSubstBigIntObject<T> = {
  [P in keyof T]: SubstBigInt<T[P]>;
};

export function maybeBigInt(
  value: Parameters<typeof BigInt>[0] | null
): bigint | null;
export function maybeBigInt(
  value?: Parameters<typeof BigInt>[0]
): bigint | undefined;
export function maybeBigInt(
  value?: Parameters<typeof BigInt>[0] | null
): bigint | null | undefined;
export function maybeBigInt(
  value?: Parameters<typeof BigInt>[0] | null
): bigint | null | undefined {
  if (value === null) {
    return null;
  }
  if (value === undefined) {
    return undefined;
  }
  return BigInt(value);
}

// This is BigInt(2) ** BigInt(120) - BigInt(1)
export const int96MaxValue = 1329227995784915872903807060280344575n;
export const int96MinValue = -int96MaxValue;

export const validateInt96ValueRange = (value: bigint) => {
  if (value < int96MinValue)
    throw new TypeError(
      `Values less than ${int96MinValue} are currently unsupported`
    );
  if (value > int96MaxValue)
    throw new TypeError(
      `Values greater than ${int96MaxValue} are currently unsupported`
    );
};

export const isInt96Value = (value: bigint): boolean =>
  value >= int96MinValue && value <= int96MaxValue;

export function validateIsBigInt(value: bigint): asserts value is bigint {
  try {
    BigInt(value);
  } catch (err: unknown) {
    throw new TypeError(`Expected bigint. Got: ${value}`);
  }
}

export const isBigInt = (value: unknown) => {
  try {
    BigInt(value as bigint);
    return true;
  } catch (err: unknown) {
    return false;
  }
};

export const bigIntToString = (value: bigint): string => {
  validateIsBigInt(value);
  validateInt96ValueRange(value);
  return value.toString();
};

export const toBigInt = (value: unknown): bigint => {
  const bigIntValue = BigInt(value as bigint);
  validateInt96ValueRange(bigIntValue);
  return bigIntValue;
};
